import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function SmoobuRawReservationPanel() {
  const [reservationId, setReservationId] = useState('124615341');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const fetchRaw = async () => {
    if (!reservationId.trim()) return;
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.smoobuRawReservation(reservationId.trim());
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5 text-blue-500" />
          Smoobu Raw Reservation Fetch
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Fetches a single reservation directly from the Smoobu API and returns
          the raw, unmodified JSON alongside our DB record for comparison.
          Use this to inspect cancellation signals and fields the sync path may discard.
        </p>

        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Reservation ID
            </label>
            <Input
              value={reservationId}
              onChange={(e) => setReservationId(e.target.value)}
              placeholder="e.g. 124615341"
              className="font-mono"
            />
          </div>
          <Button onClick={fetchRaw} disabled={loading || !reservationId.trim()}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Fetch
          </Button>
        </div>

        {results?.error && (
          <div className="text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="font-medium">Error</p>
            <p>{results.error}</p>
            {results.detail && (
              <pre className="mt-2 text-xs overflow-x-auto">{results.detail}</pre>
            )}
          </div>
        )}

        {results?.smoobu_api_response && (
          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">Smoobu API Response (raw)</p>
              <pre className="overflow-x-auto border rounded-lg p-3 text-xs bg-muted/30 max-h-[32rem] overflow-y-auto">
                {JSON.stringify(results.smoobu_api_response, null, 2)}
              </pre>
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium">
                DB Record {results.db_record ? '' : '(not found)'}
              </p>
              {results.db_record ? (
                <pre className="overflow-x-auto border rounded-lg p-3 text-xs bg-muted/30 max-h-[32rem] overflow-y-auto">
                  {JSON.stringify(results.db_record, null, 2)}
                </pre>
              ) : (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  No matching record in smoobu_reservations for ID {results.reservation_id}.
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
